<template>
    <div
        class="single-select"
        ref="rootEl"
        :style="`height: ${selectHeight}px`"
        :class="{ 'ss-disabled': disabled }"
    >
        <div
            class="select-search"
            :class="{ 'ss-unviewable': !(searchable && open) }"
            @click="toggleDropdown"
            v-if="!disabled"
        >
            <input
                ref="searchInput"
                maxlength="255"
                v-model="query"
                @focus="openDropdown"
                @keydown="closeDropdownOnTab"
                @keyup.down="movePointer(true)"
                @keyup.up="movePointer(false)"
                @keyup.enter="selectFromKeyboard"
            />
        </div>
        <div class="selected-value" v-show="!(searchable && open)" @click="toggleDropdown">
            <span class="placeholder-value" v-if="!value && !multiple">{{ placeholder }}</span>
            <slot name="multiple-selected" v-bind:selected="value" v-if="multiple">
                {{ multipleLabel ? multipleLabel : $t('selecionado') }} ({{
                    value && value.length ? value.length : 0
                }})
            </slot>
            <slot name="selected" v-bind:option="value" v-else>
                <span v-if="useML" :title="value ? $t(getLabel(value)) : ''">
                    {{ value ? $t(getLabel(value)) : '' }}
                </span>
                <span v-else :title="getLabel(value)">{{ getLabel(value) }}</span>
            </slot>
        </div>
        <div class="icons">
            <b-icon
                v-if="loading"
                class="loading-icon"
                icon="loading"
                type="is-gray"
                custom-class="load-spin"
            />
            <b-icon
                v-if="
                    open
                        ? query
                        : (multiple ? value && value.length > 0 : value) && !disabled && clearable
                "
                class="control-icons"
                icon="close"
                type="is-gray"
                size="is-small"
                @click.native="remove"
            />
            <b-icon
                class="control-icons"
                :icon="open ? 'chevron-up' : 'chevron-down'"
                type="is-gray"
                @click.native="toggleDropdown"
            />
        </div>
        <div name="dropdown-fade" v-if="paginate">
            <div
                class="ss-drop"
                :class="{ down: !dropup, up: dropup, 'ss-drop-extended': extended }"
                ref="dropdown"
                v-if="open"
                :style="`max-height: ${dropdownMaxHeight}px; ${dropup ? 'bottom: ' : 'top: '}${
                    selectHeight - 3
                }px`"
            >
                <div
                    v-for="(o, i) in paginateOptions"
                    class="ss-dropdown-options"
                    :class="{
                        selected: checkIfSelected(o),
                        'ss-hover': pointer === i,
                        'display-flex': displayFlex,
                    }"
                    :title="
                        tooltip ? (useML ? (o ? $t(getLabel(o)) : '') : getLabel(o)) : null
                    "
                    :key="i"
                    @click="selectOption(o)"
                    @mouseover="pointer = i"
                >
                    <slot name="option" v-bind:option="o">
                        <span class="option-label" v-if="useML">{{
                            o ? $t(getLabel(o)) : ''
                        }}</span>
                        <span class="option-label" v-else>{{ getLabel(o) }}</span>
                    </slot>
                    <b-icon v-if="multiple" :icon="checkIfSelected(o) ? 'minus' : 'plus'" />
                </div>
                <div
                    ref="containerScroll"
                    v-if="
                        paginate &&
                        (totalResults > paginateOptions.length || totalResults == 0) &&
                        !isLoadPage
                    "
                >
                    <div
                        style="width: 10px; height: 10px"
                        ref="infinitescrolltrigger"
                        id="scoll-trigger"
                    ></div>
                </div>
                <div class="icons">
                    <b-icon
                        v-if="paginate && isLoadPage"
                        class="loading-icon"
                        icon="loading"
                        type="is-gray"
                        custom-class="load-spin"
                    />
                </div>
            </div>
        </div>
        <div name="dropdown-fade" v-else-if="!paginate">
            <div
                class="ss-drop"
                :class="{ down: !dropup, up: dropup, 'ss-drop-extended': extended }"
                ref="dropdown"
                v-if="open && !loading"
                :style="`max-height: ${dropdownMaxHeight}px; ${dropup ? 'bottom: ' : 'top: '}${
                    selectHeight - 3
                }px`"
            >
                <div
                    v-for="(o, i) in searchable ? filtered : options"
                    class="ss-dropdown-options"
                    :class="{
                        selected: checkIfSelected(o),
                        'ss-hover': pointer === i,
                        'display-flex': displayFlex,
                    }"
                    :title="
                        tooltip ? (useML ? (o ? $t(getLabel(o)) : '') : getLabel(o)) : null
                    "
                    :key="i"
                    @click="selectOption(o)"
                    @mouseover="pointer = i"
                >
                    <slot name="option" v-bind:option="o">
                        <span class="option-label" v-if="useML">{{
                            o ? $t(getLabel(o)) : ''
                        }}</span>
                        <span class="option-label" v-else>{{ getLabel(o) }}</span>
                    </slot>
                    <b-icon v-if="multiple" :icon="checkIfSelected(o) ? 'minus' : 'plus'" />
                </div>
            </div>
        </div>
        <div name="dropdown-fade" v-if="paginate">
            <div
                class="ss-drop"
                :class="{ down: !dropup, up: dropup }"
                v-if="paginateOptions && !paginateOptions.length && open && !loading"
                :style="`${dropup ? 'bottom: ' : 'top: '}${selectHeight - 3}px`"
            >
                <div class="ss-no-options">
                    {{ noResultsMessage || $t('sem-resultados-filtro') }}
                </div>
            </div>
        </div>
        <div name="dropdown-fade" v-else-if="!paginate">
            <div
                class="ss-drop"
                :class="{ down: !dropup, up: dropup }"
                v-if="
                    (searchable ? filtered && !filtered.length : options && !options.length) &&
                    open &&
                    !loading
                "
                :style="`${dropup ? 'bottom: ' : 'top: '}${selectHeight - 3}px`"
            >
                <div class="ss-no-options">
                    {{ noResultsMessage || $t('sem-resultados-filtro') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        value: {
            type: [Object, String, Array],
            default: null,
        },
        options: {
            type: Array,
            default: () => [],
        },
        pageOptions: {
            type: Array,
            default: () => [],
        },
        label: {
            type: String,
            default: 'label',
        },
        totalResults: {
            type: Number,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        paginate: {
            type: Boolean,
            default: false,
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        closeOnSelect: {
            type: Boolean,
            default: true,
        },
        placeholder: {
            type: String,
            default: '',
        },
        dropdownMaxHeight: {
            type: Number,
            default: 200,
        },
        selectHeight: {
            type: Number,
            default: 34,
        },
        useML: {
            type: Boolean,
            default: false,
        },
        dropup: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        tooltip: {
            type: Boolean,
            default: true,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        multipleLabel: {
            type: String,
            default: null,
        },
        noResultsMessage: {
            type: String,
            default: null,
        },
        displayFlex: {
            type: Boolean,
            default: true,
        },
        extended: {
            type: Boolean,
            default: false,
        },
        //esse atributo funcina para que o array de selecionados e o array total sejam comparados pelo id e não pelo objeto
        compareId: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            search: '',
            pointer: -1,
            open: false,
            isLoadPage: false,
            query: '',
            observer: null,
            filteredOpts: [],
            debounceSelect: null,
            paginateOptions: [],
        };
    },
    created() {
        window.addEventListener('click', this.clickEvt);
    },
    mounted() {
        this.paginate ? this.emitObserver('') : this.emitSearch('');
    },
    beforeDestroy() {
        window.removeEventListener('click', this.clickEvt);
    },
    watch: {
        query(val) {
            this.paginate ? this.emitObserver(val) : this.emitSearch(val);
        },
        pageOptions: {
            deep: true,
            handler: function () {
                if (this.paginate) {
                    if (this.paginateOptions && this.paginateOptions.length == 0) {
                        this.paginateOptions = [...this.pageOptions];
                    } else {
                        this.paginateOptions = [...this.paginateOptions, ...this.pageOptions];
                    }
                    this.isLoadPage = false;
                    this.$forceUpdate();
                }
            },
        },
    },
    methods: {
        movePointer(inc) {
            if (inc) {
                if (
                    this.pointer < this.options.length - 1 ||
                    (this.paginate && this.pointer < this.paginateOptions.length - 1)
                ) {
                    this.pointer++;
                }
            } else {
                if (this.pointer > 0) {
                    this.pointer--;
                }
            }
        },
        clickEvt(e) {
            let path = e.path || e.composedPath();

            if (this.open && !path.includes(this.$refs.rootEl)) {
                this.open = false;
                this.$emit('blur');
            }
        },
        selectOption(o) {
            if (this.closeOnSelect) {
                this.open = false;
            }

            if (this.multiple) {
                // Se não estiver selecionado, inclui
                if (!this.checkIfSelected(o)) {
                    // Verifica se é um array para incluir o valor selecionado
                    if (Array.isArray(this.value)) {
                        let a = [...this.value, o];
                        this.$emit('input', a);
                        // Se não for cria um novo
                    } else {
                        let a = [];
                        a.push(o);
                        this.$emit('input', a);
                    }
                    // Se sim, remove
                } else {
                    let v = [...this.value];
                    let idx = this.value.findIndex((e) => {
                        return this.compareValues(o, e);
                    });

                    if (idx > -1) {
                        v.splice(idx, 1);
                    }

                    this.$emit('input', v);
                }
            } else {
                this.$emit('input', o);
            }
        },
        selectFromKeyboard() {
            if (this.paginate && this.pointer < this.paginateOptions.length) {
                this.selectOption(this.paginateOptions[this.pointer]);
            } else if (!this.paginate && this.pointer < this.options.length) {
                this.selectOption(this.options[this.pointer]);
            }
        },
        remove() {
            if (!this.open) {
                if (this.closeOnSelect) {
                    this.open = false;
                }

                if (this.multiple) {
                    this.$emit('input', []);
                } else {
                    this.$emit('input', null);
                }
            } else {
                this.query = '';
            }
        },
        toggleDropdown() {
            if (!this.disabled) {
                this.open = !this.open;

                if (this.open) {
                    this.$nextTick((_) => {
                        if (this.$refs.searchInput) {
                            this.$refs.searchInput.focus();
                        }
                    });
                    if (this.pointer < 0) this.pointer = 0;
                }

                if (!this.open) {
                    this.$emit('blur');
                }
            }
        },
        openDropdown() {
            if (!this.disabled) {
                this.open = true;
                if (this.paginate) {
                    this.scrollTrigger();
                }
                this.$nextTick((_) => {
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus();
                    }
                });
                if (this.pointer < 0) this.pointer = 0;
                this.$emit('focus');
            }
        },
        closeDropdownOnTab(e) {
            if (e.keyCode == 9) {
                this.open = false;
                this.$emit('blur');
            }
        },
        getLabel(o) {
            if (typeof o === 'string' || o == null) {
                return o;
            } else if (typeof o === 'object') {
                if (o[this.label] != undefined) {
                    return o[this.label];
                } else {
                    console.warn(
                        `[SELECT] A propriedade [${this.label}] não existe no objeto de opção!`
                    );
                    return '';
                }
            }
        },
        checkIfSelected(o) {
            if (this.multiple) {
                if (Array.isArray(this.value)) {
                    return this.value.some((e) => {
                        return this.compareValues(o, e);
                    });
                } else {
                    return false;
                }
            } else {
                return this.compareValues(o, this.value);
            }
        },
        // emitSearch(busca) {
        //     let val = this.isNull(busca) ? '' : this.removeToSearch(busca);
        //     console.log(val)
        //     this.query = val;
        //     this.pointer = 0;
        //     this.$emit('search', val);
        // },
        emitSearch(busca) {
            clearTimeout(this.debounceSelect);
            this.debounceSelect = setTimeout(() => {
                let val = this.isNull(busca) ? '' : this.removeToSearch(busca);
                this.pointer = 0;
                this.search = val;
                this.$emit('search', val);
            }, 600);
        },
        emitObserver(busca) {
            clearTimeout(this.debounceSelect);
            this.debounceSelect = setTimeout(() => {
                let val = this.isNull(busca) ? '' : this.removeToSearch(busca);
                this.pointer = 0;
                this.search = val;
                this.paginateOptions = [];
                this.$emit('observer', val);
            }, 600);
        },
        bindValue(o) {
            if (typeof o == 'string') {
                return o;
            }
        },
        compareValues(o, e) {
            if (typeof o == 'string' && typeof e == 'string') {
                return o == e;
            } else if (
                typeof o != 'undefined' &&
                typeof e != 'undefined' &&
                typeof o == 'object' &&
                typeof e == 'object'
            ) {
                if (this.compareId) {
                    return o.id == e.id;
                }
                return JSON.stringify(o) == JSON.stringify(e);
            } else {
                return false;
            }
        },
        scrollTrigger(busca = '') {
            if (open) {
                this.observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            this.$emit('observer', busca);
                        }
                    });
                });
                if (this.observer && this.$refs.infinitescrolltrigger) {
                    this.observer.observe(this.$refs.infinitescrolltrigger);
                }
            }
        },
    },
    computed: {
        filtered() {
            let itens = this.paginate ? this.paginateOptions : this.options;

            if (!this.searchable) {
                return itens.filter((o) => {
                    let q = this.query ? this.query.toLocaleLowerCase() : '';

                    if (typeof o == 'string') {
                        return this.useML
                            ? this.$t(o).toLocaleLowerCase().includes(q)
                            : o.toLocaleLowerCase().includes(q);
                    } else {
                        if (!o[this.label]) {
                            console.warn(
                                `[SELECT] A propriedade ${this.label} não existe dentro do objeto de opção.`
                            );
                            return false;
                        } else {
                            return this.useML
                                ? this.$t(o[this.label]).toLocaleLowerCase().includes(q)
                                : o[this.label].toLocaleLowerCase().includes(q);
                        }
                    }
                });
            } else {
                return itens;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.single-select {
    position: relative;
    display: flex;
    align-items: center;
    border: 1px solid $table-border;
    border-radius: 3px;
    background: $sovis-white;
    height: 36px;

    .selected-value {
        cursor: pointer;
        display: flex;
        align-items: center;
        width: 100%;
        flex-grow: 1;
        padding: 0.25rem 0 0.25rem 0.25rem;
        color: $darker-gray-color;
        overflow: hidden;
        height: 100%;

        span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            &.placeholder-value {
                opacity: 0.5;
            }
        }
    }

    .select-search {
        padding: 0.25rem;
        background-color: transparent;
        width: 100%;
        flex-grow: 1;

        input {
            background: transparent;
            width: 100%;
            border: none;
            outline: none;
            color: $darker-gray-color;

            &:focus {
                box-shadow: none;
            }
        }

        // Esconde o input mas ainda permite que ele seja acessível por tab
        &.ss-unviewable {
            width: 0;
            padding: 0;

            input {
                color: transparent;
            }
        }
    }

    .icons {
        display: flex;
        align-items: center;
        justify-content: center;
        padding-right: 0.25rem;

        .control-icons {
            cursor: pointer;
            width: 20px;
            height: 100%;

            &:hover {
                color: $primary-color !important;
            }
        }

        .loading-icon {
            width: 20px;
            height: 100%;
            margin-right: 0.25rem;
            margin-left: 0.25rem;
            animation-name: spin;
            animation-duration: 1000ms;
            animation-iteration-count: infinite;
            animation-timing-function: linear;
        }
    }

    .ss-drop {
        z-index: 10;
        position: absolute;
        width: calc(100% + 2px);
        left: -1px;
        color: $darker-gray-color;
        border-right: 1px solid $table-border;
        border-left: 1px solid $table-border;
        background: $sovis-white;
        overflow-y: auto;

        &.down {
            border-top: 1px solid $sovis-white;
            border-bottom: 1px solid $table-border;
            border-bottom-left-radius: 3px;
            border-bottom-right-radius: 3px;
            box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.1);
        }

        &.up {
            border-top: 1px solid $table-border;
            border-bottom: 1px solid $sovis-white;
            border-top-left-radius: 3px;
            border-top-right-radius: 3px;
            box-shadow: 0px -6px 10px rgba(0, 0, 0, 0.1);
        }

        .ss-dropdown-options {
            justify-content: space-between;
            align-items: center;
            padding: 0.25rem 0.5rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            &.ss-hover {
                cursor: pointer;
                background-color: $primary-color-hover;
                color: $sovis-white;
            }
            &.selected {
                background-color: $primary-color-click;
                color: $sovis-white;
            }
            &.selected.ss-hover {
                background-color: $primary-color;
            }
            span.option-label {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
        .ss-dropdown-options.display-flex {
            display: flex;
        }

        .ss-no-options {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.5rem;
        }
    }

    .ss-drop-extended {
        width: calc(100% + 102px) !important;
    }

    .dropdown-fade-enter-active,
    .dropdown-fade-leave-active {
        transition: opacity 0.2s ease;
    }

    .dropdown-fade-enter,
    .dropdown-fade-leave-to {
        opacity: 0;
    }

    &.ss-disabled {
        // pointer-events: none;
        cursor: text;
        background: $table-border;
        opacity: 0.6;
    }
}
</style>
